<template>
    <div
        class="ppv2-flex ppv2-flex-wrap ppv2-w-full ppv2-step-wrapper md:ppv2-gap-[3.125vw]"
        :style="{ paddingLeft: normalizeSize(55) }"
    >
        <!-- Form Section -->
        <div class="form-section">
            <HeaderStep :step="step" :sizes="sizes" />

            <!-- Stripe Info Banner -->
            <InfoBanner
                v-if="!is_stripe_configured"
                class="ppv2-mb-6"
                variant="warning"
                text="Vous devez activer Stripe pour créer un abonnement."
                linkText="Configurer Stripe"
                linkUrl="/back-office/v2/establishment/1/edit#billing:stripeSetup"
            />

            <!-- Nom de l'abonnement -->
            <CustomInput
                :label="$t('serie.step1.name_label')"
                :required="true"
                v-model="formData.name"
                :placeholder="$t('serie.step1.name_placeholder')"
                :state="getState('name')"
                :errorText="errors.name"
                @input="clearError('name')"
                @focus="activeField = 'name'"
                @blur="activeField = null"
            />

            <!-- Description -->
            <BaseTextarea
                :label="$t('serie.step1.description_label')"
                v-model="formData.description"
                :placeholder="$t('serie.step1.description_placeholder')"
                :useEditor="true"
                @focus="setActiveField('description')"
                @blur="clearActiveField('description')"
                iconIA
                :rows="5"
                :state="getState('description')"
                :error="errors.description"
                :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                customLabelClass="poppinsLight ppv2-text-black"
                classContainerAdd="ppv2-mb-[20px]"
                :required="false"
            />

            <!-- Image Upload -->
            <div class="upload-container" @mouseenter="activeField = 'image'" @mouseleave="activeField = null">
                <CustomInputUpload
                    :label="$t('serie.step1.image_label')"
                    v-model="formData.image"
                    :required="false"
                    :state="getState('image')"
                    :errorText="errors.image"
                    @input="onFileSelected"
                />
            </div>

            <!-- Navigation Buttons -->
            <div class="form-actions" :class="{ '!ppv2-justify-end': complited }">
                <BackButton v-show="!complited" type="button" @click="handleBackClick" />
                <BaseButton id="submit" type="button" @click="submitForm" variant="primary" :loading="loading">
                    {{ complited ? $t('common.save') : $t('experience.next') }}
                </BaseButton>
            </div>
        </div>

        <!-- Exit Popup -->
        <CustomPopup
            v-model="showExitPopup"
            :defaultTitle="popupTitle"
            heightClass="ppv2-max-h-[40vh]"
            customClassTitle="poppinsSemiBold"
        >
            <template #content>
                <p class="ppv2-text-center ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">
                    {{ $t('experience.descriptionClosePopup') }}
                </p>
            </template>
            <template #actions>
                <BaseButton
                    type="button"
                    variant="outline"
                    @click="exit()"
                    :customHeight="43"
                    customClass="!ppv2-min-h-[40px]"
                    :isSaveBtn="true"
                >
                    {{ $t('experience.btnLeave') }}
                </BaseButton>
                <BaseButton
                    type="button"
                    variant="primary"
                    @click="showExitPopup = false"
                    :customHeight="43"
                    customClass="!ppv2-min-h-[40px]"
                    :isSaveBtn="true"
                    :isLargeBtn="true"
                >
                    {{ $t('experience.btnStay') }}
                </BaseButton>
            </template>
        </CustomPopup>

        <!-- Preview Panel -->
        <PreviewSerie :activeField="activeField" :step="step" />
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import normalizeSize from '../../../../utils/normalizeSize'
import HeaderStep from './HeaderStep.vue'
import PreviewSerie from './PreviewSerie.vue'
import CustomInput from '../../../Inputs/CustomInput.vue'
import BaseTextarea from '../../../Shared/BaseTextarea.vue'
import CustomInputUpload from '../../../Inputs/CustomInputUpload.vue'
import BaseButton from '../../../Shared/BaseButton.vue'
import BackButton from '../../../Shared/BackButton.vue'
import CustomPopup from '../../../Shared/CustomDialog.vue'
import InfoBanner from '../../../Shared/InfoBanner.vue'
import normalizeFontSize from '../../../../utils/normalizeFontSize'

export default {
    name: 'SerieStep1',
    components: {
        HeaderStep,
        PreviewSerie,
        CustomInput,
        BaseTextarea,
        CustomInputUpload,
        BaseButton,
        BackButton,
        CustomPopup,
        InfoBanner,
    },
    props: {
        step: { type: Object, required: true },
        sizes: { type: Object, required: true },
        id: { type: [Number, String], default: null },
        complited: { type: [Boolean], default: false },
        is_stripe_configured: { type: [Boolean], default: false },
    },
    data() {
        return {
            activeField: null,
            errors: {},
            showExitPopup: false,
            popupTitle: this.$t('experience.titleClosePopup'),
            initialDataString: null,
        }
    },
    computed: {
        ...mapGetters('serie', ['formData', 'isEditMode', 'isDirty', 'loading']),
        formData: {
            get() {
                return this.$store.getters['serie/formData']
            },
            set(value) {
                this.$store.dispatch('serie/setFormData', value)
            },
        },
    },
    mounted() {
        this.initialDataString = this.getFormDataString()
    },
    methods: {
        normalizeFontSize,
        ...mapActions('serie', ['setFormData', 'saveSerie']),
        normalizeSize,

        clearError(field) {
            if (this.errors[field]) {
                delete this.errors[field]
            }
        },

        setActiveField(field) {
            this.activeField = field
        },

        clearActiveField(field) {
            if (this.activeField === field) {
                this.activeField = null
            }
        },

        onFileSelected(file) {
            if (!file) return

            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif']
            if (!allowedTypes.includes(file.type)) {
                this.$set(this.errors, 'image', this.$t('experience.step2.errors.imageOnly'))
                return
            }

            this.clearError('image')
            const reader = new FileReader()
            reader.onload = () => {
                this.setFormData({
                    image: reader.result,
                    imagePreview: reader.result,
                })
            }
            reader.readAsDataURL(file)
        },

        getState(field) {
            if (this.errors[field]) return 'error'
            if (this.formData[field]) return 'valid'
            return 'default'
        },

        getFormDataString() {
            return JSON.stringify({
                name: this.formData.name,
                description: this.formData.description,
                image: this.formData.image,
            })
        },

        hasChanges() {
            if (!this.initialDataString) return false
            return this.initialDataString !== this.getFormDataString()
        },

        resetInitialState() {
            this.initialDataString = this.getFormDataString()
        },

        handleBackClick() {
            if (this.hasChanges()) {
                this.showExitPopup = true
            } else {
                this.exit()
            }
        },

        exit() {
            const baseUrl = `${window.location.protocol}//${window.location.host}`
            window.location.href = `${baseUrl}/back-office/v3/experience`
        },

        async submitForm() {
            this.errors = {}

            // Local validation
            if (!this.formData.name || !this.formData.name.trim()) {
                this.errors.name = this.$t('serie.step1.validation.name_required')
                return
            }

            // Save to API with error handling
            try {
                await this.saveSerie()

                this.initialDataString = this.getFormDataString()
                this.$toast.success(
                    this.id ? this.$t('serie.notifications.success_update') : this.$t('serie.notifications.success_add')
                )
                if (!this.complited) {
                    this.$emit('next-step')
                }

                // Only emit next-step if save was successful
            } catch (error) {
                this.$toast.error(error.response.data.message)
                // Display field-specific validation errors if present
                if (error.response?.data?.errors) {
                    this.errors = error.response.data.errors
                }
            }
        },

        async saveStep(redirect = false) {
            try {
                // Utilisé pour "Enregistrer et quitter" : on effectue une sauvegarde complète
                await this.$store.dispatch('serie/saveSerie')
                this.initialDataString = this.getFormDataString()
                if (redirect) {
                    this.exit()
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error saving serie:', error)
            }
        },
    },
}
</script>

<style scoped>
.form-section {
    flex: 1;
}

.form-actions {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    margin-top: 40px;
}
</style>
